// What the Cases screen keeps while it is open, outside the URL: which step of
// which Case is opened in place. The list sets it (a step's row opens the Case
// with that step open) and the Case reads and changes it.
//
// Promise: an opening asked from outside the Case (the list) brings the step's
// title to the top of the detail, or the Case's top when nothing is open in it;
// an opening from inside the Case leaves the screen where it is.
import {
  createContext,
  type ReactNode,
  useContext,
  useMemo,
  useState,
} from "react";

export interface Opened {
  caseId: string;
  /** null: nothing open in place (the Case is shown from its top). */
  stepId: string | null;
  /** Put the caret into this field of the step (a step just added). */
  focus: "body" | null;
  /** Asked from outside the Case (the list): bring it into view once. */
  reveal: boolean;
}

export interface OpenOptions {
  focus?: "body";
  reveal?: boolean;
}

export interface CasesScreenState {
  opened: Opened | null;
  openStep(caseId: string, stepId: string | null, options?: OpenOptions): void;
  /** The reveal was done (the detail scrolled once). */
  revealed(): void;
  /** The Case was left: its caret request is spent (not used again on return). */
  focusUsed(caseId: string): void;
}

const Context = createContext<CasesScreenState>({
  opened: null,
  openStep: () => {},
  revealed: () => {},
  focusUsed: () => {},
});

export function CasesScreenProvider({ children }: { children: ReactNode }) {
  const [opened, setOpened] = useState<Opened | null>(null);
  const value = useMemo<CasesScreenState>(
    () => ({
      opened,
      openStep: (caseId, stepId, options = {}) =>
        setOpened({
          caseId,
          stepId,
          focus: options.focus ?? null,
          reveal: options.reveal ?? false,
        }),
      revealed: () =>
        setOpened((o) => (o?.reveal ? { ...o, reveal: false } : o)),
      focusUsed: (caseId) =>
        setOpened((o) =>
          o?.caseId === caseId && o.focus ? { ...o, focus: null } : o,
        ),
    }),
    [opened],
  );
  return <Context.Provider value={value}>{children}</Context.Provider>;
}

export const useCasesScreen = () => useContext(Context);

/** How the screen asks this Case to show (null for another Case or none). */
export function useOpened(caseId: string | null): Opened | null {
  const { opened } = useCasesScreen();
  return caseId !== null && opened?.caseId === caseId ? opened : null;
}
